import type {
  GoalListRecord,
  QueuedMutation,
  SyncState,
} from "../../shared/domain";
import { api, ApiError } from "./api";
import {
  cachedList,
  enqueue,
  queuedForUser,
  removeQueued,
  replaceCachedList,
  saveConflict,
  saveRecovery,
  updateQueuedRetry,
  type CachedList,
} from "./offline-db";
import { applyLocally } from "./local-mutations";

type SyncResult =
  | { status: "applied" | "duplicate"; operationId: string; version: number }
  | {
      status: "conflict";
      operationId: string;
      version: number;
      authoritative: GoalListRecord | null;
    }
  | {
      status: "period-closed";
      operationId: string;
      recoveryDraftId: string;
      authoritative: GoalListRecord | null;
    };

type Listener = (state: SyncState, detail: string) => void;
type ClientMutationFields =
  | "operationId"
  | "idempotencyKey"
  | "userId"
  | "listId"
  | "logicalPeriodKey"
  | "baseVersion"
  | "clientCreatedAt"
  | "sequence"
  | "retry";
export type ClientMutation = QueuedMutation extends infer Operation
  ? Operation extends QueuedMutation
    ? Omit<Operation, ClientMutationFields>
    : never
  : never;

export class SyncEngine {
  private running = false;
  private listeners = new Set<Listener>();
  constructor(readonly userId: string) {}

  subscribe(listener: Listener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
  private announce(state: SyncState, detail: string) {
    for (const listener of this.listeners) listener(state, detail);
  }

  async queue(list: CachedList, input: ClientMutation) {
    if (list.ownerId !== this.userId)
      throw new Error("This list belongs to another account.");
    const queued = await queuedForUser(this.userId);
    const operationId = crypto.randomUUID();
    const operation = {
      ...input,
      operationId,
      idempotencyKey: `${this.userId}.${operationId}`,
      userId: this.userId,
      listId: list.id,
      logicalPeriodKey: list.logicalPeriodKey,
      baseVersion: list.optimisticVersion,
      clientCreatedAt: new Date().toISOString(),
      sequence: (queued.at(-1)?.sequence ?? 0) + 1,
      retry: { attempts: 0, nextAttemptAt: null, lastError: null },
    } as QueuedMutation;
    const next = applyLocally(list, operation);
    await enqueue(operation);
    await replaceCachedList(this.userId, next);
    this.announce(
      navigator.onLine ? "device" : "waiting",
      navigator.onLine ? "Saved on this device" : "Waiting for connection",
    );
    await this.registerBackgroundSync();
    if (navigator.onLine) void this.flush();
    return next;
  }

  async flush() {
    if (this.running || !navigator.onLine) return;
    this.running = true;
    this.announce("syncing", "Syncing changes");
    try {
      const operations = await queuedForUser(this.userId);
      for (const operation of operations) {
        if (operation.userId !== this.userId)
          throw new Error("Queue account mismatch.");
        try {
          const response = await api<{ results: SyncResult[] }>(
            "/api/mutations",
            {
              method: "POST",
              body: JSON.stringify({
                clientVersion: "1",
                operations: [operation],
              }),
            },
          );
          const result = response.results[0];
          if (!result) throw new Error("Missing synchronization result.");
          const local =
            (await cachedList(this.userId, operation.listId)) ?? null;
          if (result.status === "conflict") {
            await saveConflict({
              id: crypto.randomUUID(),
              userId: this.userId,
              operation,
              authoritative: result.authoritative,
              local,
              createdAt: new Date().toISOString(),
            });
            this.announce("conflict", "A change needs your review");
            break;
          }
          if (result.status === "period-closed") {
            await saveRecovery({
              id: crypto.randomUUID(),
              userId: this.userId,
              sourceListId: operation.listId,
              logicalPeriodKey: operation.logicalPeriodKey,
              operations: [operation],
              serverDraftId: result.recoveryDraftId,
              createdAt: new Date().toISOString(),
            });
            await removeQueued(this.userId, operation.operationId);
            this.announce(
              "period-closed",
              "Server period closed; your work was preserved",
            );
            continue;
          }
          await removeQueued(this.userId, operation.operationId);
        } catch (error) {
          if (error instanceof ApiError && error.status === 401) {
            this.announce("waiting", "Sign in as the same account to sync");
            break;
          }
          await updateQueuedRetry(
            this.userId,
            operation.operationId,
            error instanceof Error ? error.message : "Could not sync",
          );
          this.announce("failed", "Could not sync");
          break;
        }
      }
      if ((await queuedForUser(this.userId)).length === 0)
        this.announce("synced", "Synced");
    } finally {
      this.running = false;
    }
  }

  private async registerBackgroundSync() {
    const registration = await navigator.serviceWorker?.ready;
    if (!registration || !("sync" in registration)) return;
    await (
      registration as ServiceWorkerRegistration & {
        sync: { register(tag: string): Promise<void> };
      }
    ).sync.register("10thingz-sync");
  }
}
